/**
 * **演出の確認ページ**（Issue #522 / #523 / #643。`/duel-editor`）。演出とテーマの見た目を確かめて詰める。
 *
 * - 演出の一覧と ▶（その演出の区間を繰り返し再生）・サンプルの試合（`?scene=<id>`。動画なし・rAF の時計）
 * - テーマを詰める道具（lil-gui・舞台を押して部品を選ぶ編集の層）・試しに動かす
 * - フィールド・盤の層・オブジェクト・HUD・カメラ（数値で詰める）をパネルで切り替える
 *
 * **時刻を合わせる作業は別のページ**（タイミング調節 `/duel-timing`。`pages/DuelTiming.js`）。ここは何も保存しない
 * （テーマの見た目の値だけは端末の localStorage。確定は「テーマとして書き出す」→ テーマのファイルに貼る）。
 * 舞台の組み立ては `useDuelStage`（タイミング調節と共通）。`?duel=&game=` で試合を開ける。
 *
 * 合わせ方: フィールド＝実写でゾーンの枠（`lines`）を出し、**枠が実写のカードに重なるか**を見る。
 * 重ならなければ実写の座標系（`duelEditor/fields/live.js`）か投影（`stage/camera.js`）が違う。
 *
 * **パネルは互いに独立**（ユーザー判断 2026-09-24）—— フィールドを切り替えてもカメラと層の濃さは動かない。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';
import { useDuelStage } from './useDuelStage';
import Canvas, { CANVAS } from '../duelEditor/canvas/Canvas';
import { CAMERA_FIELDS, DEFAULT_SHOT_NAME, SHOTS, SHOT_LABELS } from '../duelEditor/stage/camera';
import { FIELD_LAYERS, OBJECT_KINDS, OBJECT_PRESETS, OPACITY_PRESETS } from '../duelEditor/core/theme';
import { objectItems } from '../duelEditor/stage/ObjectLayer';
import { buildGui, exportText, loadStyle, saveStyle } from './duelEditorGui';
import { toDuelScene } from '../editorPorts/duelScene';
import { EASES } from '../duelEditor/core/effect';
import { BINARY_LAYERS } from '../editorPorts/timingSettings';
import { DEFAULT_THEME, THEMES, themeByName } from '../duelEditor/themes';
import {
  clipRange, effectRows, loopSeek, nextClip, sampleHead, sampleLoaded,
} from './duelEditorTuning';
import FpsMeter from '../duelTimeline/FpsMeter';
import PreviewSettings from '../duelTimeline/PreviewSettings';
import StageVideo from '../duelTimeline/StageVideo';
import { useSpaceKey } from '../duelTimeline/useSpaceKey';
import {
  PREVIEW_QUALITIES, STAGE_HEIGHT, loadPreviewPrefs, qualityById, savePreviewPrefs,
} from '../editorPorts/previewQuality';

/**
 * フィールドオブジェクトの選択（**どの Field を作るかだけ**）。
 * カメラと層の濃さは**独立**（ユーザー判断 2026-09-24）—— 同じカメラのまま実写と仮想を見比べられる。
 * 「真上 / 斜め」は Field の違いではなくカメラのプリセットなので、仮想は 1 つ。
 */
const FIELD_CHOICES = [
  { id: 'live', label: '実写（overlay_zones）' },
  { id: 'virtual', label: '仮想（理想のグリッド）' },
  { id: 'empty', label: '空（ゾーンが分からない試合）' },
];

/** 「試しに動かす」の素の値（`duelEditor/` の外から板を触る口を目で見るため。Issue #523） */
const TWEAK_BASE = Object.freeze({
  raise: 0, dz: 0, dx: 0, scale: 1, rot: 0, opacity: 1, brightness: 1, saturation: 1,
  tint: '#ffffff', hidden: false, attack: false,
});

/** スライダーの一覧（この表が一次情報。足せば勝手に出る） */
const TWEAK_FIELDS = Object.freeze([
  { key: 'raise', label: '持ち上げ', min: 0, max: 400, step: 1 },
  { key: 'dz', label: '前後', min: -600, max: 600, step: 1 },
  { key: 'dx', label: '左右', min: -600, max: 600, step: 1 },
  { key: 'scale', label: '倍率', min: 0.2, max: 3, step: 0.05 },
  { key: 'rot', label: '回し', min: -3.15, max: 3.15, step: 0.05 },
  { key: 'opacity', label: '濃さ', min: 0, max: 1, step: 0.05 },
  { key: 'brightness', label: '明るさ', min: 0, max: 3, step: 0.05 },
  { key: 'saturation', label: '彩度', min: 0, max: 3, step: 0.05 },
]);

/** 「攻撃！を試す」の 1 周（ms）。**時間はページが持つ**（`duelEditor/` は時計を持たない） */
const ATTACK_MS = 1200;

const box = { border: '1px solid #ccc', borderRadius: 8, padding: 10, marginBottom: 10 };
/*
 * **操作パネルは自分でスクロールする**（Issue #523。ユーザー 2026-09-24
 * 「テーマを詰めるが縦長すぎて画面をみながら作業できない」）。
 *
 * 共通のトランスフォームと飾りが全部品に付いたのでノブが数百になり、フォルダを 1 つ開くだけで
 * パネルが画面より縦に長くなる。ページごとスクロールすると**舞台が画面から出ていく**ので、
 * 詰めた結果が見られない。**舞台は貼り付けたまま、パネルの中だけが流れる**ようにする。
 *
 * `position: sticky` が効くのは `.page` に `overflow` が無いから（付けると黙って効かなくなる）。
 */
const panel = {
  flex: '0 1 360px',
  minWidth: 280,
  position: 'sticky',
  top: 'calc(var(--nav-h) + 8px)',
  maxHeight: 'calc(100dvh - var(--nav-h) - 32px)',
  overflowY: 'auto',
  // パネルの端まで流しても後ろのページが動き出さない
  overscrollBehavior: 'contain',
  // スクロールバーが出たり消えたりで中身の幅が揺れないように
  scrollbarGutter: 'stable',
  paddingRight: 6,
};
const h4 = { margin: '0 0 8px', fontSize: 14 };

export default function DuelEditorDemo() {
  usePageTitle('演出の確認');

  const [params] = useState(() => new URLSearchParams(window.location.search));
  const [duels, setDuels] = useState([]);
  const [duelId, setDuelId] = useState(() => params.get('duel') ?? '');
  const [gameUuid, setGameUuid] = useState(() => params.get('game') ?? '');
  const [videoError, setVideoError] = useState(null);
  const [listError, setListError] = useState(null);

  const [choice, setChoice] = useState('live');
  const [opacity, setOpacity] = useState(OPACITY_PRESETS.live);
  const [objectOpacity, setObjectOpacity] = useState(OBJECT_PRESETS.live);
  const [tweakTarget, setTweakTarget] = useState('');
  const [tweakOpts, setTweakOpts] = useState(TWEAK_BASE);
  // **既定のカメラは 90° 回転・斜め**（ユーザー判断 2026-09-25。`stage/camera.js::DEFAULT_SHOT_NAME`）
  const [shot, setShot] = useState(SHOTS[DEFAULT_SHOT_NAME]);
  const [zoneLabels, setZoneLabels] = useState(true);
  const [hudOpts, setHudOpts] = useState({ show: true });
  /** **テーマの見た目の値**（Issue #523）。宣言は `theme.schema` で、**全部の層に届く** */
  const [themeStyle, setThemeStyle] = useState({});
  /** テーマを詰めるモード（舞台で部品を押して選ぶ ＋ lil-gui を出す。Issue #523） */
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stageScale, setStageScale] = useState(1);
  const [exported, setExported] = useState('');
  const [themeName, setThemeName] = useState(DEFAULT_THEME.name);
  const theme = themeByName(themeName);

  /** `?scene=<id>` で演出の一覧の場面をサンプルとして開く（録画用。Issue #585） */
  const sampleScene = params.get('scene');
  const [sampleMode, setSampleMode] = useState(Boolean(sampleScene));
  /** **値で固定する**（Issue #543。呼ぶたびに新しい object だと source が組み直され続ける） */
  const sample = useMemo(
    () => (sampleMode ? { loaded: sampleLoaded(sampleScene), head: sampleHead(sampleScene) } : null),
    [sampleMode, sampleScene],
  );

  // プレビューの大きさ・軽さ・fps の表示（端末ごと。タイミング調節と同じ設定を共有）
  const [preview, setPreview] = useState(() => loadPreviewPrefs());
  const changePreview = useCallback((next) => { setPreview(next); savePreviewPrefs(next); }, []);
  const previewStage = qualityById(preview.quality).stage;
  const stageExtra = useMemo(() => ({ ...previewStage, style: themeStyle }), [previewStage, themeStyle]);

  const settings = useMemo(() => ({
    field: choice, shot, hud: hudOpts.show !== false, zoneLabels, fieldLayers: opacity, objectLayers: objectOpacity,
  }), [choice, shot, hudOpts, zoneLabels, opacity, objectOpacity]);

  /*
   * **試しに動かす**（Issue #523）。外から板を触る口が効くことを目で見るためのもの。
   * **そのフレームからその場で組む**（state に溜めると 1 フレーム遅れてちらつく）ので、フレームの関数で渡す。
   */
  const tweaksFor = useCallback((frameNow) => {
    if (!tweakTarget) return null;
    const o = tweakOpts;
    // **イージングは差す側の仕事**。時間はページが持ち、`duelEditor/` には値だけ渡る
    const t = o.attack ? EASES.ease_out(((frameNow?.ms ?? 0) % ATTACK_MS) / ATTACK_MS) : 0;
    const swing = o.attack ? Math.sin(t * Math.PI) : 0;
    return {
      [tweakTarget]: ({ root, stack, face, fades, fx, reset }) => {
        reset();
        if (o.hidden) { root.visible = false; return; }
        root.position.x += o.dx;
        root.position.z += o.dz - swing * 220;
        root.rotation.y += o.rot;
        root.scale.set(o.scale, o.scale, o.scale);
        stack.position.y = o.raise + swing * 90;
        if (fx) {
          fx.brightness.value = o.brightness + swing * 1.6;
          fx.saturation.value = o.saturation;
        }
        face.material.color.set(o.tint);
        if (o.opacity < 1) face.material.opacity = o.opacity;
        void fades;
      },
    };
  }, [tweakTarget, tweakOpts]);
  const extra = useCallback((frameNow) => ({
    objectTweaks: tweaksFor(frameNow), editing, selected, scale: stageScale, onSelect: setSelected,
  }), [tweaksFor, editing, selected, stageScale]);
  const stage = useDuelStage({
    gameUuid, replayId: params.get('replay') ? Number(params.get('replay')) : null,
    sample, theme, settings, stage: stageExtra, extra,
  });
  const {
    match, movieUrl, editor, activeClock, togglePlay, playing, videoEl, videoSize, setVideoSize, attachVideo,
    seOn, setSeOn, live, field, frame, renderConfig, layers, filter,
  } = stage;
  const { cardName, note } = match;
  // Space で再生 / 一時停止（サンプルの試合も。文字を打つ欄の中では奪わない）
  useSpaceKey(togglePlay);
  const error = listError ?? match.error;

  /*
   * **外から動かす口**（Issue #543）。演出の見た目を Playwright で撮るときに、その帯の頭へ飛んで止める。
   */
  useEffect(() => {
    window.duelEditorDebug = {
      // 実試合は元動画の時計が editor を押すので、**時計のほうを**動かす（Issue #549）
      seek: (ms) => { activeClock.pause(); activeClock.seek(ms); editor.seek(ms); },
      clips: () => (editor.timeline?.items ?? []).map(({ id, type, startMs, endMs, payload }) => ({
        id, type, startMs, endMs, payload: JSON.parse(JSON.stringify(payload ?? {}, (k, v) => (k === 'data' ? undefined : v))),
      })),
    };
    return () => { delete window.duelEditorDebug; };
  }, [editor, activeClock]);

  useEffect(() => {
    api.project('GetDuelListProject').then((rows) => setDuels(rows ?? [])).catch((e) => setListError(String(e?.message ?? e)));
  }, []);
  const duel = duels.find((d) => String(d.id) === duelId) ?? null;
  const games = duel?.games ?? [];

  /*
   * **作業中の写し**（Issue #523）。詰めた値はテーマごとに localStorage に残る
   * （リロードしても消えない）。確定は「テーマとして書き出す」→ テーマのファイルに貼る。
   */
  useEffect(() => { setThemeStyle(loadStyle(theme.name)); setExported(''); }, [theme.name]);
  useEffect(() => { saveStyle(theme.name, { ...theme.style, ...themeStyle }, theme.style); }, [theme, themeStyle]);

  /*
   * **lil-gui は宣言（`theme.schema`）から作る**ので、テーマを足せばノブが勝手に増える。
   * **動的 import**（素朴に入れると全員のバンドルに乗る）。`onChange` で React の state へ写す。
   */
  const guiBox = useRef(null);
  const guiRef = useRef(null);
  useEffect(() => {
    if (!editing) return undefined;
    let alive = true;
    const values = { ...theme.style, ...loadStyle(theme.name) };
    import('lil-gui').then(({ default: GUI }) => {
      if (!alive || !guiBox.current) return;
      guiRef.current = buildGui(GUI, guiBox.current, {
        schema: theme.schema,
        parts: theme.parts,
        values,
        onChange: (next) => setThemeStyle(next),
      });
    }).catch(() => {});
    return () => {
      alive = false;
      guiRef.current?.gui?.destroy?.();
      guiRef.current = null;
    };
  }, [editing, theme]);

  /** 舞台で選んだ部品のフォルダを開き、見える位置まで送る（Issue #523） */
  useEffect(() => {
    const folders = guiRef.current?.folders;
    if (!folders) return;
    Object.entries(folders).forEach(([part, f]) => (part === selected ? f.open() : f.close()));
    const opened = selected ? folders[selected] : null;
    opened?.domElement?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);

  /*
   * **演出を詰めるための足場**（Issue #536）。演出は帯が出ている瞬間しか画面に無いので、
   * 「その演出へ飛ぶ / その区間を繰り返し再生する」が要る。
   */
  const rows = useMemo(() => effectRows(editor.timeline, theme.effects), [editor.timeline, theme.effects]);
  const [loopRange, setLoopRange] = useState(null);
  const playEffect = useCallback((row) => {
    const clip = nextClip(row.clips, editor.transport.ms);
    const range = clipRange(clip);
    if (!range) return;
    setLoopRange(range);
    activeClock.seek(range.startMs);
    activeClock.play(1);
  }, [editor, activeClock]);
  /** **折り返しは時計の外**（`duelEditorTuning::loopSeek` は純関数） */
  useEffect(() => {
    if (!loopRange) return undefined;
    return activeClock.subscribe((ms) => {
      const to = loopSeek(ms, loopRange);
      if (to != null) activeClock.seek(to);
    });
  }, [loopRange, activeClock]);

  /** 試しに動かす対象の一覧（盤に置かれているもの） */
  const tweakTargets = useMemo(() => {
    const scene = toDuelScene(frame, renderConfig);
    return objectItems(scene.board, scene).map((item) => ({
      id: item.id,
      label: item.kind === 'card' || item.kind === 'pile'
        ? `${item.zone}${item.card?.code || item.top?.code ? `（${cardName(item.card?.code ?? item.top?.code) ?? item.card?.code ?? item.top?.code}）` : ''}`
        : item.id,
    }));
  }, [frame, renderConfig, cardName]);

  return (
    <div className="page" style={{ maxWidth: 'none' }}>
      <StageVideo
        src={movieUrl} videoRef={attachVideo} onError={setVideoError}
        onSize={(s) => setVideoSize((prev) => (prev?.w === s.w && prev?.h === s.h ? prev : s))}
      />

      <div className="page-header">
        <h2>演出の確認（duelEditor）</h2>
        <span style={{ color: '#666', fontSize: 12 }}>
          演出とテーマの見た目を確かめて詰める。時刻合わせは
          {' '}
          <a href={`/duel-timing${duelId && gameUuid ? `?duel=${duelId}&game=${gameUuid}` : ''}`}>タイミング調節</a>
        </span>
      </div>

      {error ? <div className="card" style={{ color: '#c00' }}>{error}</div> : null}
      {videoError ? <div className="card" style={{ color: '#c00', whiteSpace: 'pre-wrap' }}>{`元動画が読めません: ${videoError}`}</div> : null}

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 900px', minWidth: 320 }}>
          <div style={{ position: 'relative' }}>
            <Canvas canvas={CANVAS} maxHeight={preview.stageHeight} layers={layers} onScale={setStageScale} filter={filter} />
            {preview.fps ? <FpsMeter ms={frame.ms} playing={playing} fps={editor.doc.fps ?? 30} /> : null}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <button type="button" onClick={togglePlay} disabled={!movieUrl && !sampleMode} title="Space">再生 / 停止</button>
            <button type="button" onClick={() => { if (videoEl) videoEl.currentTime = Math.max(0, videoEl.currentTime - 5); }} disabled={!movieUrl}>-5 秒</button>
            <button type="button" onClick={() => { if (videoEl) videoEl.currentTime += 5; }} disabled={!movieUrl}>+5 秒</button>
            <span style={{ fontSize: 12, color: '#666' }}>
              {videoSize ? `動画 ${videoSize.w}×${videoSize.h}` : '動画の寸法待ち'}
              {` / ${(editor.transport.ms / 1000).toFixed(2)} 秒`}
              {frame.states?.hud?.turn > 0 ? ` / TURN ${frame.states.hud.turn} ${frame.states.hud.phaseLabel ?? ''}` : ''}
              {live.reason ? ` / ${live.reason}` : (live.source === 'config' ? ' / overlay_zones' : '')}
              {note ? ` / ${note}` : ''}
            </span>
          </div>
        </div>

        <div style={panel} data-testid="de-panel">
          <div style={box}>
            <h4 style={h4}>試合</h4>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <select value={duelId} onChange={(e) => { setDuelId(e.target.value); setGameUuid(''); }}>
                <option value="">デュエル…</option>
                {duels.map((d) => <option key={d.id} value={String(d.id)}>{d.name ?? `#${d.id}`}</option>)}
              </select>
              <select value={gameUuid} onChange={(e) => setGameUuid(e.target.value)} disabled={!games.length || sampleMode}>
                <option value="">試合…</option>
                {games.map((g, i) => <option key={g.game_uuid} value={g.game_uuid}>{g.name || `試合 ${i + 1}`}</option>)}
              </select>
            </div>
            {/*
              * **演出を詰めるのはサンプルの試合で**（Issue #536）。実試合を読むと
              * 「融合の演出を詰めたい」たびに融合が出てくる試合を探すことになる。
              * core も動画も要らない（`editorPorts/samples/sampleDuel.json`）。
              */}
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
              <input type="checkbox" checked={sampleMode} onChange={(e) => setSampleMode(e.target.checked)} />
              サンプルの試合で演出を詰める（動画なし・rAF の時計）
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
              <input type="checkbox" data-testid="se-on" checked={seOn} onChange={(e) => setSeOn(e.target.checked)} />
              SE を鳴らす（演出の sounds）
            </label>
            <div style={{ marginTop: 6 }}>
              <PreviewSettings qualities={PREVIEW_QUALITIES} value={preview} onChange={changePreview} heightRange={STAGE_HEIGHT} />
            </div>
          </div>

          {/* **演出の一覧**（そのフレームではなく試合ぜんぶ）。▶ でその区間を繰り返し再生 */}
          <div style={box}>
            <h4 style={h4}>演出（{theme.label}）</h4>
            {rows.length === 0 ? <p style={{ margin: 0, opacity: 0.7 }}>このテーマに演出はありません</p> : null}
            {rows.map((row) => (
              <div key={row.event} style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ opacity: row.count ? 1 : 0.45 }}>
                  {row.label}
                  <span style={{ opacity: 0.6, fontSize: '0.85em' }}>{` ${row.event} ×${row.count}`}</span>
                </span>
                <button type="button" onClick={() => playEffect(row)} disabled={!row.count} data-testid={`fx-play-${row.event}`}>▶</button>
              </div>
            ))}
            {loopRange ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <span style={{ fontSize: '0.85em', opacity: 0.7 }}>
                  {`繰り返し ${(loopRange.startMs / 1000).toFixed(2)}〜${(loopRange.endMs / 1000).toFixed(2)} 秒`}
                </span>
                <button type="button" onClick={() => { setLoopRange(null); activeClock.pause(); }}>やめる</button>
              </div>
            ) : null}
          </div>

          <div style={box}>
            <h4 style={h4}>テーマ</h4>
            <select value={themeName} onChange={(e) => setThemeName(e.target.value)} data-testid="de-theme">
              {THEMES.map((t) => <option key={t.name} value={t.name}>{t.label}</option>)}
            </select>
            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              舞台の見た目一式（盤の層の描き方 ＋ HUD）。**層が何であるかは core** なので、
              テーマを切り替えても下の濃さはそのまま
            </div>
          </div>

          <div style={box}>
            <h4 style={h4}>テーマを詰める</h4>
            {/*
              **テーマを作る道具**（Issue #523）。AI が初稿を書き、ここで人が数値を詰め、
              「テーマとして書き出す」で**テーマのファイルに固定**する。
              ノブの一覧は `theme.schema` なので、テーマを足せば勝手に増える。
            */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={editing} data-testid="editing" onChange={(e) => { setEditing(e.target.checked); setSelected(null); }} />
              舞台を押して部品を選ぶ
            </label>
            <div ref={guiBox} style={{ marginTop: 6 }} />
            {editing ? (
              <>
                <div style={{ fontSize: 12, color: '#666', margin: '6px 0' }}>
                  {selected ? `選択中: ${selected}` : '舞台の部品を押すと、その部品のノブが開きます'}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" data-testid="theme-export" onClick={() => setExported(exportText({ ...theme.style, ...themeStyle }, theme.style))}>
                    テーマとして書き出す
                  </button>
                  <button type="button" data-testid="theme-reset" onClick={() => { setThemeStyle({}); setExported(''); setEditing(false); }}>
                    既定に戻す
                  </button>
                </div>
                {exported ? (
                  <textarea
                    readOnly
                    data-testid="theme-exported"
                    value={exported}
                    style={{ width: '100%', height: 120, marginTop: 6, fontSize: 11, fontFamily: 'monospace' }}
                  />
                ) : null}
              </>
            ) : null}
          </div>

          <div style={box}>
            <h4 style={h4}>フィールド</h4>
            {FIELD_CHOICES.map((c) => (
              <label key={c.id} style={{ display: 'block', fontSize: 13 }}>
                <input type="radio" name="de-field" checked={choice === c.id} onChange={() => setChoice(c.id)} />
                {' '}{c.label}
              </label>
            ))}
            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              {field.known ? `ゾーン ${field.keys.length} 個 / カード 1 枚の幅 ${Math.round(field.unit)}px` : 'ゾーンが分かりません（枠とカードは出ません）'}
            </div>
          </div>

          <div style={box}>
            <h4 style={h4}>盤</h4>
            {/* 層の一覧・ラベルは `core/theme` の宣言から来る（層を足すとここに勝手に増える） */}
            {FIELD_LAYERS.map((layer) => {
              // **テーマが描き方を持たない層は描かれない**（core の一覧には在る）
              const drawn = typeof theme.field[layer.name] === 'function';
              return (
                <label key={layer.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: drawn ? 1 : 0.45 }}>
                  <span style={{ width: 96 }}>{layer.label}</span>
                  <input
                    type="range" min={0} max={1} step={0.05}
                    disabled={!drawn}
                    value={opacity[layer.name] ?? 0}
                    onChange={(e) => setOpacity((o) => ({ ...o, [layer.name]: Number(e.target.value) }))}
                  />
                  <span style={{ width: 52, textAlign: 'right' }}>
                    {drawn ? (opacity[layer.name] ?? 0).toFixed(2) : '描かない'}
                  </span>
                </label>
              );
            })}
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              {Object.keys(OPACITY_PRESETS).map((name) => (
                <button key={name} type="button" onClick={() => setOpacity(OPACITY_PRESETS[name])}>
                  {name === 'live' ? '実写向け' : '仮想向け'}
                </button>
              ))}
            </div>
            <label style={{ display: 'block', fontSize: 13, marginTop: 4 }}>
              <input type="checkbox" checked={zoneLabels} onChange={(e) => setZoneLabels(e.target.checked)} /> 枠にキーを出す
            </label>
          </div>

          <div style={box}>
            <h4 style={h4}>オブジェクト</h4>
            {/* 盤に**置かれているもの**。種類の一覧は `core/theme`（テーマを切り替えても設定は生きる） */}
            {OBJECT_KINDS.map((kind) => {
              const drawn = typeof theme.objects[kind.name] === 'function';
              // **カードは 出す / 出さない**（出さない間は motion の演出を呼ばない。ユーザー判断 2026-10-01）
              if (BINARY_LAYERS.includes(kind.name)) {
                return (
                  <label key={kind.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: drawn ? 1 : 0.45 }}>
                    <span style={{ width: 96 }}>{kind.label}</span>
                    <input
                      type="checkbox" disabled={!drawn} data-testid={`de-object-${kind.name}`}
                      checked={(objectOpacity[kind.name] ?? 0) >= 0.5}
                      onChange={(e) => setObjectOpacity((o) => ({ ...o, [kind.name]: e.target.checked ? 1 : 0 }))}
                    />
                    <span style={{ fontSize: 12, color: '#666' }}>{drawn ? '出さないと motion も呼ばない' : '描かない'}</span>
                  </label>
                );
              }
              return (
                <label key={kind.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: drawn ? 1 : 0.45 }}>
                  <span style={{ width: 96 }}>{kind.label}</span>
                  <input
                    type="range" min={0} max={1} step={0.05}
                    disabled={!drawn}
                    value={objectOpacity[kind.name] ?? 0}
                    onChange={(e) => setObjectOpacity((o) => ({ ...o, [kind.name]: Number(e.target.value) }))}
                  />
                  <span style={{ width: 52, textAlign: 'right' }}>
                    {drawn ? (objectOpacity[kind.name] ?? 0).toFixed(2) : '描かない'}
                  </span>
                </label>
              );
            })}
            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              ocgCore の状態（カード・山・実写の手札）。**動きは最小**で、時間で動く演出は別の層
            </div>

            {/*
              **試しに動かす**（Issue #523）。演出の層が来るまでのあいだ、外から板を触る口
              （`tweaks`：生の three のオブジェクトを渡す）が効くことを目で見るためのもの。
            */}
            <div style={{ borderTop: '1px solid #eee', marginTop: 10, paddingTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <strong style={{ fontSize: 13 }}>試しに動かす</strong>
                <button type="button" onClick={() => { setTweakOpts(TWEAK_BASE); setTweakTarget(''); }}>リセット</button>
              </div>
              {tweakTargets.length === 0 ? (
                <div style={{ fontSize: 12, color: '#666' }}>動かせるものがありません（盤面に何も出ていない）</div>
              ) : (
                <>
                  <select
                    value={tweakTarget}
                    onChange={(e) => setTweakTarget(e.target.value)}
                    style={{ width: '100%', marginBottom: 6 }}
                    data-testid="tweak-target"
                  >
                    <option value="">（触らない）</option>
                    {tweakTargets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  {TWEAK_FIELDS.map((f) => (
                    <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      <span style={{ width: 66 }}>{f.label}</span>
                      <input
                        type="range" min={f.min} max={f.max} step={f.step}
                        disabled={!tweakTarget}
                        value={tweakOpts[f.key]}
                        data-testid={`tweak-${f.key}`}
                        onChange={(e) => setTweakOpts((o) => ({ ...o, [f.key]: Number(e.target.value) }))}
                      />
                      <span style={{ width: 46, textAlign: 'right' }}>{tweakOpts[f.key]}</span>
                    </label>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, marginTop: 4, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      色
                      <input type="color" disabled={!tweakTarget} value={tweakOpts.tint}
                        onChange={(e) => setTweakOpts((o) => ({ ...o, tint: e.target.value }))} />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="checkbox" disabled={!tweakTarget} checked={tweakOpts.hidden}
                        onChange={(e) => setTweakOpts((o) => ({ ...o, hidden: e.target.checked }))} />
                      隠す
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="checkbox" disabled={!tweakTarget} checked={tweakOpts.attack}
                        data-testid="tweak-attack"
                        onChange={(e) => setTweakOpts((o) => ({ ...o, attack: e.target.checked }))} />
                      攻撃！を試す
                    </label>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                    演出は**生の three のオブジェクト**を受け取って直接触る。**借りている間はその板は演出のもの**で、
                    離せば器が素に戻す。「攻撃！」の時間とイージングは**ページが持つ**（`duelEditor/` は時計を持たない）
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={box}>
            <h4 style={h4}>HUD</h4>
            <label style={{ display: 'block', fontSize: 13 }}>
              <input type="checkbox" checked={hudOpts.show} onChange={(e) => setHudOpts((o) => ({ ...o, show: e.target.checked }))} /> 表示する
            </label>
            <label style={{ display: 'block', fontSize: 13 }}>
              <input type="checkbox" checked={themeStyle.showHand ?? theme.style.showHand} onChange={(e) => setThemeStyle((o) => ({ ...o, showHand: e.target.checked }))} /> 手札を出す
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 4 }}>
              <span>アイコン</span>
              <select value={themeStyle.portraitSource ?? theme.style.portraitSource} onChange={(e) => setThemeStyle((o) => ({ ...o, portraitSource: e.target.value }))}>
                <option value="deck">デッキのイラスト</option>
                <option value="player">プレイヤーの画像</option>
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 4 }}>
              <span>枠の内側の辺</span>
              <select value={String(themeStyle.plateSlope ?? theme.style.plateSlope)} onChange={(e) => setThemeStyle((o) => ({ ...o, plateSlope: e.target.value === 'board' ? 'board' : Number(e.target.value) }))}>
                <option value="board">盤の辺に合わせる</option>
                <option value="0.3">0.3（固定）</option>
                <option value="0">まっすぐ</option>
              </select>
            </label>
            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              盤の左右に余る場所に置く（舞台は完成動画と同じ 1920×1080）。
              「盤の辺に合わせる」はカメラを振っても枠が盤に沿う
            </div>
          </div>

          <div style={box}>
            <h4 style={h4}>カメラ</h4>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              {Object.keys(SHOTS).map((name) => (
                <button key={name} type="button" onClick={() => setShot(SHOTS[name])}>{SHOT_LABELS[name]}</button>
              ))}
            </div>
            {CAMERA_FIELDS.map((f) => {
              const v = shot[f.key] ?? 0;
              return (
                <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <span style={{ width: 86 }}>{f.label}</span>
                  <input
                    type="range" min={f.min} max={f.max} step={f.step} value={v}
                    onChange={(e) => setShot((s) => ({ ...s, [f.key]: Number(e.target.value) }))}
                  />
                  <span style={{ width: 56, textAlign: 'right' }}>{Math.round(v * 10) / 10}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
